import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { login, socialLogin, fetchUser } from '../../store/slices/authSlice';
import { Form, Button, Container, Card, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import { GoogleLogin } from '@react-oauth/google';
import {
    FaHeart,
    FaEnvelope,
    FaLock,
    FaSpinner,
    FaEye,
    FaEyeSlash,
    FaCheckCircle,
    FaShieldAlt,
    FaRocket,
    FaUsers,
    FaChartLine,
    FaClock,
    FaBolt
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import LandingNavbar from '../../components/layout/LandingNavbar';
import LandingFooter from '../../components/layout/LandingFooter';
import './Auth.css';


const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(login(formData)).unwrap();

      // Fetch updated user data to check profile completion
      await dispatch(fetchUser());

      toast.success('✅ Connexion réussie ! Bienvenue sur LOVA 💕');

      // Check if profile is complete and redirect accordingly
      const user = JSON.parse(localStorage.getItem('user'));
      if (user && user.profile && user.profile.is_complete) {
        navigate('/home');
      } else {
        navigate('/complete-profile');
      }

    } catch (err) {
      console.error('Login failed:', err);
      if (err.message && err.message.includes('verify')) {
        navigate('/verify?identifier=' + formData.identifier);
      }
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const result = await dispatch(socialLogin({ provider: 'google', token: credentialResponse.credential })).unwrap();
      const user = result.user;

      if (user && user.profile && user.profile.is_complete) {
        toast.success('\u2705 Connexion Google r\u00e9ussie ! Bienvenue sur LOVA \ud83d\udc95');
        navigate('/home');
      } else {
        toast.success('\u2705 Connexion Google r\u00e9ussie ! Compl\u00e9tez votre profil pour continuer \ud83d\udc95');
        navigate('/complete-profile');
      }
    } catch (err) {
      console.error('Google login failed:', err);
      toast.error('❌ Connexion Google échouée : ' + (err?.message || 'Erreur inconnue'));
    }
  };

  const handleGoogleError = () => {
    toast.error('❌ Connexion Google annulée ou échouée');
  };

  return (
    <div className="auth-page-wrapper">
      <LandingNavbar />
      <div className="auth-page">
        <Container>

        <Row className="justify-content-center py-5">
          <Col lg={5} xl={6}>
            <Card className="auth-card shadow-lg">
              <Row className="g-0">
                <Col md={12} className="auth-form-section">

                  <div className="auth-form-content">
                    <h3 className="auth-form-title">Bienvenue de retour 💕</h3>
                    <p className="auth-form-subtitle">
                      Connectez-vous pour continuer votre aventure amoureuse
                    </p>

                    {/* Error Message */}
                    {error && (
                      <Alert variant="danger" className="auth-alert">
                        <strong>Erreur :</strong> {error.message || 'Une erreur est survenue'}
                      </Alert>
                    )}

                    <Form onSubmit={handleSubmit} className="auth-form">
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label-custom">
                          Email ou nom d'utilisateur
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="input-icon-group">
                            <FaEnvelope />
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            name="identifier"
                            value={formData.identifier}
                            onChange={(e) => setFormData({...formData, identifier: e.target.value})}
                            required
                            className="form-control-custom"
                            placeholder="votre@email.com ou username"
                          />
                        </InputGroup>
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label className="form-label-custom">
                          Mot de passe
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="input-icon-group">
                            <FaLock />
                          </InputGroup.Text>
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            className="form-control-custom"
                            placeholder="Votre mot de passe"
                          />
                          <InputGroup.Text
                            className="input-icon-group password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{ cursor: 'pointer' }}
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </InputGroup.Text>
                        </InputGroup>
                      </Form.Group>

                      <div className="form-options mb-4">
                        <Form.Check
                          type="checkbox"
                          label="Se souvenir de moi"
                          className="remember-me"
                        />
                        <Link to="/forgot-password" className="forgot-password">
                          Mot de passe oublié ?
                        </Link>
                      </div>

                      <Button
                        type="submit"
                        className="btn-auth-submit w-100"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Connexion en cours...
                          </>
                        ) : (
                          <>
                            <FaBolt className="me-2" />
                            Retrouver mes Loves 💕
                          </>
                        )}
                      </Button>
                    </Form>

                    <div className="auth-divider">
                      <span>OU</span>
                    </div>

                    <div className="auth-social-buttons">
                      <div className="google-btn-wrapper mb-2">
                        <GoogleLogin
                          onSuccess={handleGoogleSuccess}
                          onError={handleGoogleError}
                          width="400"
                          shape="rectangular"
                          text="continue_with"
                          locale="fr"
                        />
                      </div>
                    </div>

                    <div className="auth-footer">
                      <p>
                        Nouveau sur LOVA ?{' '}
                        <Link to="/register" className="auth-link">
                          Créer mon profil LOVE 💕
                        </Link>
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Container>

        {/* Background Decoration */}
        <div className="auth-background">
          <div className="floating-heart heart-1">❤️</div>
          <div className="floating-heart heart-2">💕</div>
          <div className="floating-heart heart-3">💖</div>
          <div className="floating-heart heart-4">💗</div>
          <div className="floating-heart heart-5">💓</div>
          <div className="floating-heart heart-6">💘</div>
        </div>
      </div>
      <LandingFooter />
    </div>
  );

};

export default Login;
